<script>
import {ref, onMounted} from "vue";
import {Cropper} from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";
import "vue-advanced-cropper/dist/theme.compact.css";
import {useSearchStore} from "@js/store/SearchStore.js";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import IconX from "@js/components/Icons/IconX.vue";
import ImageSearchResultItem from "@js/components/Sections/ImageSearch/ImageSearchResultItem.vue";
import axios from "axios";
import {router} from "@inertiajs/vue3";
import IconCamera from "@js/components/Icons/IconCamera.vue";
import IconResize from "@js/components/Icons/IconResize.vue";
import CatalogProductSkeleton from "@js/components/Sections/Catalog/ProductItem/CatalogProductSkeleton.vue";

export default {
    name: "ImageSearchResults",
    components: {
        CatalogProductSkeleton,
        IconResize,
        IconCamera,
        Cropper,
        OnlyMobile,
        IconX,
        ImageSearchResultItem,
    },
    data() {
        return {
            cropper_props: {
                aspectRatio: null,
                handlers: {
                    top: true,
                    right: true,
                    bottom: true,
                    left: true,
                    topLeft: true,
                    topRight: true,
                    bottomLeft: true,
                    bottomRight: true,
                    eastNorth: true,
                    north: true,
                    south: true,
                    west: true,
                    east: true,
                    westNorth: true,
                    westSouth: true,
                    eastSouth: true,
                }
            }
        }
    },
    setup() {
        const searchStore = useSearchStore();
        const modalRef = ref(null);
        const cropperRef = ref(null);

        const handleBackgroundClick = (e) => {
            if (!modalRef.value.contains(e.target)) {
                searchStore.hideImageSearchResult();
            }
        };

        onMounted(() => {
            router.on("before", () => searchStore.hideImageSearchResult());
        });

        // Отправляем обрезанное изображение на сервер
        const cropAndSearch = () => {
            const cropper = cropperRef.value;
            if (!cropper) return;
            const {canvas} = cropper.getResult();
            if (canvas) {
                canvas.toBlob((blob) => {
                    const form = new FormData()
                    form.append('photo', blob, "cropped.png")

                    searchStore.resultsLoaded = false
                    axios.post('/axios/search/search-by-image', form, {
                        headers: {'Content-Type': 'multipart/form-data'}
                    }).then(({data}) => {
                        searchStore.imageSearch.results = data
                        searchStore.resultsLoaded = true
                    }).catch(error => {
                        console.error('Image search error:', error)
                        searchStore.resultsLoaded = false
                    })
                }, "image/png");
            }
        };

        const reUpload = () => {
            searchStore.hideImageSearchResult();
            searchStore.showImageSearch();
        };

        function isTouchDevice() {
            return (
                'ontouchstart' in window ||
                navigator.maxTouchPoints > 0 ||
                navigator.msMaxTouchPoints > 0
            )
        }

        return {
            searchStore,
            modalRef,
            cropperRef,
            cropAndSearch,
            reUpload,
            handleBackgroundClick,
        };
    },
};
</script>

<template>
    <div v-if="searchStore.imageSearchResult.isOpen"
         class="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black bg-opacity-70"
         @click="handleBackgroundClick">
        <div ref="modalRef"
             :class="{ 'top-i-0': searchStore.imageSearchResult.isOpen && searchStore.imageSearchResult.animation_in_progress }"
             class="relative top-[100%] bg-white lg:rounded-2xl shadow-lg lg:max-w-[1400px] lg:max-h-[90%] w-full h-full transition-all duration-500 overflow-hidden">
            <button @click.prevent="searchStore.hideImageSearchResult"
                    class="absolute top-2 right-2 text-gray-400 md:hover:text-black">
                <IconX class="h-7 w-7"/>
            </button>
            <div class="w-full h-full p-5 overflow-y-auto mt-10 md:mt-5">
                <div class="grid grid-cols-12 gap-5">
                    <!-- Левая панель: vue-advanced-cropper -->
                    <div class="col-span-12 md:col-span-3 sticky md:top-0 md:self-start md:z-10 max-md:border-b max-md:pb-5">
                        <p class="font-semibold text-lg mb-2">Поиск по фото</p>
                        <div class="w-full h-[300px] rounded-xl overflow-hidden">
                            <cropper ref="cropperRef"
                                     :src="searchStore.imageSearch.previewUrl"
                                     :grid="true"
                                     :stencil-props="cropper_props"
                                     class="cropper w-full h-full"/>
                        </div>
                        <p class="text-sm md:text-xs font-bold text-gray-800 pt-2 pb-4">
                            Можете выбрать рамкой любой элемент на снимке
                        </p>
                        <button @click.prevent="cropAndSearch"
                                class="flex items-center justify-center leading-tight gap-2 w-full py-3 bg-baseColor md:hover:bg-baseColor/80 text-black rounded text-sm font-semibold transition">
                            <IconResize class="w-5 h-5"/>
                            Обрезать и искать
                        </button>
                        <button @click.prevent="reUpload"
                                class="flex items-center justify-center leading-tight gap-2 mt-2 w-full py-3 border rounded md:hover:bg-gray-100 text-sm font-semibold transition">
                            <IconCamera class="w-5 h-5"/>
                            Выбрать новое фото
                        </button>
                    </div>

                    <!-- Правая панель: результаты -->
                    <div class="col-span-12 md:col-span-9">
                        <p class="font-semibold text-lg mb-2">Результаты поиска:</p>
                        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 mb-10">
                            <ImageSearchResultItem v-if="searchStore.resultsLoaded"
                                                   v-for="product in searchStore.imageSearch.results"
                                                   :key="product.id"
                                                   :data="product"/>
                            <CatalogProductSkeleton v-else v-for="i in 6"/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">

</style>
